// src/services/learningService.ts
import apiClient from './api';
import { Question as LearningQuestion, UserProgress, LearningSession, DifficultyLevel, Hint } from '../types/learning';

// ========================
// Study Session & Progress (Unchanged)
// ========================

export interface StartStudySessionPayload {
  userId: string;
  subject: string;
  term: string;
  strand: string;
  substrand?: string;
}

export interface StudySessionResponse {
  id: string;
  subject: string;
  term: string;
  strand: string;
  substrand?: string;
  startTime: string;
}

export interface EndStudySessionPayload {
  completedSubStrands: string[];
  learningProgress: number;
  attentionScore: number;
}

export interface EndStudySessionResponse {
  id: string;
  duration: number; // Calculated from endTime - startTime (in milliseconds)
  completedSubStrands: string[];
  learningProgress: number;
  attentionScore: number;
}

export interface StudyProgress {
  subject: string;
  term: string;
  strand: string;
  subStrand: string;
  startTime: string;
  endTime?: string;
  status: 'active' | 'paused' | 'completed' | 'cancelled';
  learningProgress: number;
  attentionScore?: number;
}

export interface StudyOptions {
  terms: string[];
  strands: string[];
}

/**
 * Start a new study session.
 * POST /learning/session
 */
export const startStudySession = async (
  data: StartStudySessionPayload
): Promise<StudySessionResponse> => {
  try {
    const response = await apiClient.post('/learning/session', data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * End an active study session.
 * PUT /learning/session/:sessionId/end
 */
export const endStudySession = async (
  sessionId: string,
  updates: EndStudySessionPayload
): Promise<EndStudySessionResponse> => {
  try {
    const response = await apiClient.put(`/learning/session/${sessionId}/end`, updates);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Get study progress for a specific user.
 * GET /learning/progress/:userId
 */
export const getStudyProgress = async (userId: string): Promise<StudyProgress[]> => {
  try {
    const response = await apiClient.get(`/learning/progress/${userId}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Get study session options.
 * GET /learning/options
 */
export const getStudyOptions = async (): Promise<StudyOptions> => {
  try {
    const response = await apiClient.get('/learning/options');
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// ===================
// Learning Content API (Updated Endpoints)
// ===================

// Define SubStrand type (as used locally).
export interface SubStrand {
  id: string;
  name: string;
}

// Updated Strand interface to include an `id` property and use SubStrand.
export interface Strand {
  id: string;
  name: string;
  substrands: SubStrand[];
}

// Here, we consider that each Subject document contains its learning content info.
// (Depending on your backend it might be expanded later.)
export interface Subject {
  id: string;
  name: string;
  terms: string[];
  strands: Strand[];
  description?: string;
  gradeLevel?: string; // For this setup, sometimes a single grade is stored.
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateSubjectPayload {
  name: string;
  term: string;
  strands: Strand[];
  description?: string;
  gradeLevel?: string;
}

export interface UpdateSubjectPayload extends Partial<CreateSubjectPayload> {}

/**
 * Fetch all subjects (unfiltered).
 * GET /subjects
 */
export const getAllSubjects = async (): Promise<Subject[]> => {
  try {
    const response = await apiClient.get('/subjects');
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetch subjects with filtering by grade and term.
 * GET /subjects?grade=...&term=...
 */
export const getSubjects = async (grade: string, term: number): Promise<Subject[]> => {
  try {
    // Updated endpoint: removed '/learning'
    const response = await apiClient.get('/subjects', {
      params: { grade, term }
    });
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetch a specific subject by ID.
 * GET /subjects/:id
 */
export const getSubjectById = async (id: string): Promise<Subject> => {
  try {
    const response = await apiClient.get(`/subjects/${id}`);
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Create a new subject.
 * POST /subjects
 */
export const createSubject = async (payload: CreateSubjectPayload): Promise<Subject> => {
  try {
    const response = await apiClient.post('/subjects', payload);
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Update an existing subject.
 * PUT /subjects/:id
 */
export const updateSubject = async (id: string, payload: UpdateSubjectPayload): Promise<Subject> => {
  try {
    const response = await apiClient.put(`/subjects/${id}`, payload);
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Delete a subject.
 * DELETE /subjects/:id
 */
export const deleteSubject = async (id: string): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete(`/subjects/${id}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetches unfiltered learning content.
 * GET /content
 */
export const getContent = async (): Promise<string> => {
  try {
    const response = await apiClient.get('/content');
    return response.data.content;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetches an interactive exercise.
 * GET /interactive-exercise
 */
export interface Exercise {
  id: string;
  title: string;
  description: string;
}

export const getInteractiveExercise = async (): Promise<Exercise> => {
  try {
    const response = await apiClient.get('/interactive-exercise');
    return response.data.exercise;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export interface Question {
  id: string;
  question: string;
  options: string[];
  correctAnswer: string;
}

/**
 * Fetches assessment questions.
 * GET /assessment-questions
 */
export const getAssessmentQuestions = async (): Promise<Question[]> => {
  try {
    const response = await apiClient.get('/assessment-questions');
    return response.data.questions;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// ========================
// New Functions for Filtering (Updated Endpoints)
// ========================

/**
 * Fetch available Terms for a given grade.
 * GET /terms?grade=...
 */
export interface Term {
  id: string;
  name: string;
}

export const getTerms = async (grade: string): Promise<Term[]> => {
  try {
    const response = await apiClient.get('/terms', {
      params: { grade },
    });
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetch strands for a given subject, term, and grade.
 * GET /strands?subject=...&term=...&grade=...
 */
export const getStrands = async (
  subject: string,
  term: string,
  grade: string
): Promise<Strand[]> => {
  try {
    const response = await apiClient.get('/strands', {
      params: { subject, term, grade },
    });
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetch sub-strands for a given strand, subject, term, and grade.
 * GET /substrands?strand=...&subject=...&term=...&grade=...
 */
export const getSubStrands = async (
  strand: string,
  subject: string,
  term: string,
  grade: string
): Promise<SubStrand[]> => {
  try {
    const response = await apiClient.get('/substrands', {
      params: { strand, subject, term, grade },
    });
    const data = response.data.data;
    // If the response returns an array of strings instead of objects,
    // convert them to SubStrand objects.
    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
      return data.map((s: string, index: number) => ({
        id: index.toString(),
        name: s,
      }));
    }
    // Otherwise, assume the data is already in the correct format.
    return data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetch lesson content filtered by the student's grade, term, subject, strand, and sub-strand.
 * GET /lessons?grade=...&term=...&subject=...&strand=...&substrand=...
 */
export const getFilteredLessonContent = async (
  grade: string,
  term: string,
  subject: string,
  strand: string,
  substrand: string
): Promise<string> => {
  try {
    const response = await apiClient.get('/lessons', {
      params: { grade, term, subject, strand, substrand },
    });
    return response.data.data.content;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// ========================
// Assessment & Exercise Endpoints (Unchanged)
// ========================

/**
 * Utility function for handling API errors.
 */
const handleApiError = (error: unknown): void => {
  if (error instanceof Error) {
    console.error('API Error:', error.message);
  } else {
    console.error('Unexpected API Error:', error);
  }
};

export const getLessonContent = async (
  grade: string,
  term: number,
  subject: string,
  strand: string,
  subStrand: string
) => {
  // For the heart lesson specifically
  if (
    grade === "4" &&
    term === 1 &&
    subject === "Science" &&
    strand === "Human Body" &&
    subStrand === "The Heart"
  ) {
    return {
      id: "heart-lesson",
      title: "Understanding the Human Heart",
      content: {
        type: "interactive",
        component: "HeartLesson",
      },
      modules: [
        {
          id: "intro",
          type: "content",
          title: "Introduction to the Heart",
        },
        {
          id: "parts",
          type: "interactive",
          title: "Parts of the Heart",
        },
        {
          id: "experiment",
          type: "interactive",
          title: "Heart Beat Experiment",
        },
        {
          id: "quiz",
          type: "assessment",
          title: "Heart Knowledge Check",
        },
        {
          id: "assessment",
          type: "assessment",
          title: "Final Assessment",
        },
      ],
    };
  }

  // ... existing code for other lessons ...
};

export class LearningService {
  private static instance: LearningService;
  private questions: Map<string, LearningQuestion> = new Map();
  private sessions: Map<string, LearningSession> = new Map();

  private constructor() {}

  static getInstance(): LearningService {
    if (!LearningService.instance) {
      LearningService.instance = new LearningService();
    }
    return LearningService.instance;
  }

  // Add public method to load questions
  setQuestions(questions: LearningQuestion[]): void {
    questions.forEach(question => {
      this.questions.set(question.id, question);
    });
  }

  // Question Management
  async loadQuestions(category: string, gradeLevel: number): Promise<LearningQuestion[]> {
    // TODO: Implement API call to fetch questions
    return [];
  }

  // Session Management
  createSession(userId: string, category: string, difficulty: DifficultyLevel): LearningSession {
    const session: LearningSession = {
      id: crypto.randomUUID(),
      userId,
      category,
      currentQuestionId: '',
      progress: [],
      startTime: new Date(),
      difficulty,
      completedQuestions: [],
      skippedQuestions: []
    };
    this.sessions.set(session.id, session);
    return session;
  }

  // Adaptive Learning Algorithm
  getNextQuestion(sessionId: string): LearningQuestion | null {
    const session = this.sessions.get(sessionId);
    if (!session) return null;

    const completedQuestions = new Set(session.completedQuestions);
    const availableQuestions = Array.from(this.questions.values())
      .filter(q => 
        q.metadata.gradeLevel === 4 &&
        !completedQuestions.has(q.id) &&
        this.arePrerequisitesMet(q, session)
      );

    if (availableQuestions.length === 0) return null;

    // Sort by difficulty and user performance
    return this.rankQuestions(availableQuestions, session);
  }

  private arePrerequisitesMet(question: LearningQuestion, session: LearningSession): boolean {
    return question.prerequisites.every(prereqId => 
      session.completedQuestions.includes(prereqId)
    );
  }

  private rankQuestions(questions: LearningQuestion[], session: LearningSession): LearningQuestion {
    return questions.sort((a, b) => {
      const aProgress = this.getQuestionProgress(a.id, session);
      const bProgress = this.getQuestionProgress(b.id, session);
      
      // Consider difficulty, previous attempts, and time spent
      const aScore = this.calculateQuestionScore(a, aProgress);
      const bScore = this.calculateQuestionScore(b, bProgress);
      
      return aScore - bScore;
    })[0];
  }

  private getQuestionProgress(questionId: string, session: LearningSession): UserProgress | undefined {
    return session.progress.find(p => p.questionId === questionId);
  }

  private calculateQuestionScore(question: LearningQuestion, progress?: UserProgress): number {
    if (!progress) return 0;

    const attemptPenalty = progress.attempts * 0.2;
    const timePenalty = Math.min(progress.timeSpent / question.timeEstimate, 2);
    const hintPenalty = progress.hintsUsed.length * 0.1;

    return attemptPenalty + timePenalty + hintPenalty;
  }

  // Hint System
  getAvailableHints(questionId: string, sessionId: string): Hint[] {
    const session = this.sessions.get(sessionId);
    const question = this.questions.get(questionId);
    if (!session || !question) return [];

    const progress = this.getQuestionProgress(questionId, session);
    if (!progress) return [];

    const timeSpent = (Date.now() - progress.lastAttempt.getTime()) / 1000;
    
    return question.hints.filter(hint => 
      timeSpent >= hint.timeThreshold &&
      !progress.hintsUsed.includes(hint.id)
    );
  }

  // Progress Tracking
  updateProgress(
    sessionId: string,
    questionId: string,
    isCorrect: boolean,
    timeSpent: number,
    hintId?: string
  ): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    const progress = this.getQuestionProgress(questionId, session) || {
      questionId,
      attempts: 0,
      timeSpent: 0,
      hintsUsed: [],
      lastAttempt: new Date(),
      isCorrect: false
    };

    progress.attempts++;
    progress.timeSpent += timeSpent;
    progress.lastAttempt = new Date();
    progress.isCorrect = isCorrect;
    
    if (hintId) {
      progress.hintsUsed.push(hintId);
    }

    if (isCorrect) {
      session.completedQuestions.push(questionId);
    }

    session.progress = session.progress.filter(p => p.questionId !== questionId);
    session.progress.push(progress);
  }

  // Analytics
  getSessionAnalytics(sessionId: string) {
    const session = this.sessions.get(sessionId);
    if (!session) return null;

    return {
      totalQuestions: session.completedQuestions.length + session.skippedQuestions.length,
      completedQuestions: session.completedQuestions.length,
      averageAttempts: this.calculateAverageAttempts(session),
      averageTimePerQuestion: this.calculateAverageTime(session),
      difficultyProgression: this.calculateDifficultyProgression(session)
    };
  }

  private calculateAverageAttempts(session: LearningSession): number {
    if (session.progress.length === 0) return 0;
    return session.progress.reduce((sum, p) => sum + p.attempts, 0) / session.progress.length;
  }

  private calculateAverageTime(session: LearningSession): number {
    if (session.progress.length === 0) return 0;
    return session.progress.reduce((sum, p) => sum + p.timeSpent, 0) / session.progress.length;
  }

  private calculateDifficultyProgression(session: LearningSession): DifficultyLevel[] {
    return session.completedQuestions
      .map(id => this.questions.get(id)?.difficulty)
      .filter((d): d is DifficultyLevel => d !== undefined);
  }
}

export async function getQuestions(lesson: string): Promise<Question[]> {
  try {
    const response = await apiClient.get('/heart-lesson/questions', {
      params: { lesson }
    });
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export async function submitAnswer({ questionId, answer, sessionId }: { questionId: string, answer: string, sessionId: string }): Promise<{ correct: boolean, feedback: string }> {
  try {
    const response = await apiClient.post('/heart-lesson/answer', {
      questionId,
      answer,
      sessionId
    });
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export async function getHints(questionId: string, sessionId: string): Promise<{ hints: Hint[] }> {
  const res = await apiClient.get(`/hints?questionId=${questionId}&sessionId=${sessionId}`);
  if (!res.ok) throw new Error('Failed to fetch hints');
  return res.json();
}

// Voice-Enabled Learning API
export interface VoiceLearningSession {
  id: string;
  userId: string;
  subject: string;
  term: string;
  strand: string;
  substrand?: string;
  voiceEnabled: boolean;
  voiceSettings: {
    language: string;
    accent: string;
    speechRate: number;
    pitch: number;
  };
  startTime: string;
  endTime?: string;
  status: 'active' | 'paused' | 'completed';
}

export interface VoiceLearningResponse {
  transcript: string;
  confidence: number;
  isCorrect: boolean;
  feedback: string;
  nextQuestion?: string;
}

/**
 * Start a voice-enabled learning session
 * POST /learning/voice-session
 */
export const startVoiceLearningSession = async (
  data: Omit<VoiceLearningSession, 'id' | 'startTime' | 'status'>
): Promise<VoiceLearningSession> => {
  try {
    const response = await apiClient.post('/learning/voice-session', data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Process voice input for learning
 * POST /learning/voice-input
 */
export const processVoiceInput = async (
  sessionId: string,
  audioData: string
): Promise<VoiceLearningResponse> => {
  try {
    const response = await apiClient.post('/learning/voice-input', {
      sessionId,
      audioData
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Update voice learning settings
 * PUT /learning/voice-settings
 */
export const updateVoiceSettings = async (
  sessionId: string,
  settings: VoiceLearningSession['voiceSettings']
): Promise<VoiceLearningSession> => {
  try {
    const response = await apiClient.put(`/learning/voice-settings/${sessionId}`, settings);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};
